import dayjs from 'dayjs';

/**
 * Malaysia (MYT, UTC+8) date/time helpers for attendance, events, and logs.
 */
export const MALAYSIA_TZ = 'Asia/Kuala_Lumpur';

function formatParts(date, options) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: MALAYSIA_TZ, ...options }).formatToParts(date);
}

/** YYYY-MM-DD in Malaysia — use for attendance API date params (not UTC from toISOString). */
export function toMalaysiaLocalDateString(date = new Date()) {
  const parts = formatParts(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
  const pick = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

/** Parse API datetime / TIME values for display in Malaysia. */
export function parseMalaysiaDateTime(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim();
  if (!s) return null;

  const timeOnly = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeOnly) {
    const [, h, m, sec = '0'] = timeOnly;
    const base = toMalaysiaLocalDateString(new Date());
    const iso = `${base}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:${String(sec).padStart(2, '0')}+08:00`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const sql = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)/);
  if (sql && !s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(`${sql[1]}T${sql[2]}+08:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatMalaysiaTime(value, { hour12 = false } = {}) {
  const d = parseMalaysiaDateTime(value);
  if (!d) {
    const m = String(value ?? '').match(/(?:T|\s|^)(\d{1,2}:\d{2})(?::\d{2})?/);
    return m?.[1] ?? null;
  }
  return new Intl.DateTimeFormat('en-MY', {
    timeZone: MALAYSIA_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  }).format(d);
}

export function formatMalaysiaDateTime(value, options = {}) {
  const d = parseMalaysiaDateTime(value);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-MY', {
    timeZone: MALAYSIA_TZ,
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options,
  }).format(d);
}

/** Check-in/out clock display (HH:mm). */
export function formatMalaysiaAttendanceClock(value) {
  const t = formatMalaysiaTime(value);
  return t ?? '—';
}

/** Event list / details time range. */
export function formatEventTimeRange(start, end) {
  if (start == null || start === '') return 'All day';
  const a = formatMalaysiaTime(start);
  if (!a) return 'All day';
  const b = end != null && end !== '' ? formatMalaysiaTime(end) : null;
  return b ? `${a} – ${b}` : a;
}

/** Parse API / URL time values for MUI TimePicker (dayjs). */
export function toPickerTime(value) {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const d = parseMalaysiaDateTime(value);
  return d ? dayjs(d) : null;
}

/** Format TimePicker value as HH:mm for API submission. */
export function formatPickerTime(value) {
  const t = dayjs.isDayjs(value) ? value : toPickerTime(value);
  return t?.isValid() ? t.format('HH:mm') : null;
}

export function formatMalaysiaNowString() {
  return new Intl.DateTimeFormat('en-MY', {
    timeZone: MALAYSIA_TZ,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(new Date());
}
